import { MainLayout } from '@/components/layout/MainLayout';
import { Pagination } from '@/components/common/Pagination';
import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getQuotes, deleteQuote, getQuote, updateQuote, createQuote, type Quote, type QuoteDetail } from '@/lib/api/quotes';
import { createInvoiceFromQuote } from '@/lib/api/invoices';
import { getClients, type Client as ApiClient } from '@/lib/api/clients';
import { getJobs, type Job as ApiJob } from '@/lib/api/jobs';
import apiClient from '@/lib/api/client';
import { PrintableQuote } from '@/components/estimates/PrintableQuote';
import { printQuote } from '@/lib/utils/generateInvoicePdf';
import { getSettings } from '@/lib/api/settings';
import { useToast } from '@/lib/hooks/useToast';
import { ToastContainer } from '@/components/common/Toast';
import { LargeTitleHeader, SegmentedControl, PullToRefresh, SwipeableRow } from '@/components/common';
import type { SwipeAction } from '@/components/common';
import { quotePath } from '@/lib/utils/navPaths';

const LS_QUOTES_FILTER = 'quotes_status_filter';

type StatusFilter = '' | 'draft' | 'sent' | 'approved' | 'declined' | 'expired';

const STATUS_FILTER_OPTIONS: { value: StatusFilter; label: string }[] = [
  { value: '', label: 'All' },
  { value: 'draft', label: 'Draft' },
  { value: 'sent', label: 'Sent' },
  { value: 'approved', label: 'Approved' },
  { value: 'declined', label: 'Declined' },
];

export const QuotesListPage = () => {
  const navigate = useNavigate();
  const printableRef = useRef<HTMLDivElement>(null);
  const { toast, toasts, removeToast } = useToast();
  const [quotes, setQuotes] = useState<Quote[]>([]);
  const [loading, setLoading] = useState(true);
  const [printingQuote, setPrintingQuote] = useState<QuoteDetail | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>(() => {
    try { return (localStorage.getItem(LS_QUOTES_FILTER) as StatusFilter) || ''; } catch { return ''; }
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [companyInfo, setCompanyInfo] = useState<{
    name: string;
    address: string;
    phone: string;
    email: string;
    logo?: string;
  } | undefined>(undefined);
  
  // Labour Estimate Modal State
  const [showLabourModal, setShowLabourModal] = useState(false);
  const [clients, setClients] = useState<ApiClient[]>([]);
  const [jobs, setJobs] = useState<ApiJob[]>([]);
  const [filteredJobs, setFilteredJobs] = useState<ApiJob[]>([]);
  const [labourFormData, setLabourFormData] = useState({
    title: '',
    client_id: '',
    job_id: '',
    total_hours: '',
    hourly_rate: '75',
    notes: ''
  });
  const [creatingLabour, setCreatingLabour] = useState(false);

  useEffect(() => { try { localStorage.setItem(LS_QUOTES_FILTER, statusFilter); } catch {} }, [statusFilter]);

  useEffect(() => {
    loadQuotes();
  }, [searchTerm, statusFilter, currentPage]);

  useEffect(() => {
    // Load clients and jobs for the labour estimate modal
    const loadClientsAndJobs = async () => {
      try {
        const [clientsRes, jobsRes] = await Promise.all([
          getClients(),
          getJobs()
        ]);
        setClients(clientsRes || []);
        setJobs(jobsRes.jobs || []);
      } catch (error) {
        console.error('Failed to load clients/jobs:', error);
      }
    };
    loadClientsAndJobs();

    // Load company settings for printing
    getSettings()
      .then((settings) => {
        if (settings.company) {
          const c = settings.company;
          const addressParts = [
            c.address,
            c.city && c.state ? `${c.city}, ${c.state}` : c.city || c.state,
            c.zipCode,
          ].filter(Boolean);

          setCompanyInfo({
            name: c.companyName || 'OchrePoint Contracting',
            address: addressParts.join('\n'),
            phone: c.phone || '',
            email: c.email || '',
            logo: c.logoUrl,
          });
        }
      })
      .catch((err) => {
        console.error('Failed to load company settings:', err);
      });
  }, []);

  useEffect(() => {
    // Filter jobs by selected client
    if (labourFormData.client_id) {
      const clientJobs = jobs.filter(j => j.clientId === parseInt(labourFormData.client_id));
      setFilteredJobs(clientJobs);
    } else {
      setFilteredJobs([]);
    }
  }, [labourFormData.client_id, jobs]);

  const loadQuotes = async () => {
    try {
      setLoading(true);
      const response = await getQuotes({
        page: currentPage,
        limit: itemsPerPage,
        search: searchTerm || undefined,
        status: statusFilter || undefined,
      });

      // Transform API response to match component interface
      const transformedQuotes: Quote[] = response.quotes.map((q) => ({
        id: q.id,
        quote_number: q.quote_number,
        job_id: q.job_id,
        job_title: q.job_title,
        client_name: q.client_name,
        status: q.status,
        total: q.total || 0,
        subtotal: q.subtotal || 0,
        tax_rate: q.tax_rate || 0,
        tax_amount: q.tax_amount || 0,
        created_at: q.created_at,
        updated_at: q.updated_at,
      }));

      setQuotes(transformedQuotes);
      setTotalItems(response.total);
    } catch (error) {
      console.error('Failed to load quotes:', error);
      setQuotes([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  };

  const handlePrintQuote = async (quoteId: string) => {
    try {
      const data = await getQuote(quoteId);
      setPrintingQuote(data);
      // Wait for React to render the printable area
      setTimeout(() => {
        if (printableRef.current) {
          printQuote(printableRef.current);
          setPrintingQuote(null);
        }
      }, 300);
    } catch (error) {
      console.error('Failed to print quote:', error);
      toast.error('Failed to load quote details for printing');
    }
  };

  const handleCreateLabourEstimate = async () => {
    if (!labourFormData.title || !labourFormData.client_id || !labourFormData.job_id || !labourFormData.total_hours || !labourFormData.hourly_rate) {
      toast.warning('Please fill in all required fields');
      return;
    }

    try {
      setCreatingLabour(true);
      const totalAmount = parseFloat(labourFormData.total_hours) * parseFloat(labourFormData.hourly_rate);
      await apiClient.post('/labour-estimates', {
        title: labourFormData.title,
        client_id: parseInt(labourFormData.client_id),
        job_id: parseInt(labourFormData.job_id),
        total_hours: parseFloat(labourFormData.total_hours),
        hourly_rate: parseFloat(labourFormData.hourly_rate),
        total_amount: totalAmount,
        notes: labourFormData.notes
      });

      setShowLabourModal(false);
      setLabourFormData({ title: '', client_id: '', job_id: '', total_hours: '', hourly_rate: '75', notes: '' });
      toast.success('Labour estimate created successfully!');
    } catch (error) {
      console.error('Failed to create labour estimate:', error);
      toast.error('Failed to create labour estimate');
    } finally {
      setCreatingLabour(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'draft':
        return 'bg-gray-100 text-gray-600 ring-1 ring-inset ring-gray-300';
      case 'sent':
        return 'bg-blue-100 text-blue-700 ring-1 ring-inset ring-blue-200';
      case 'approved':
        return 'bg-green-100 text-green-700 ring-1 ring-inset ring-green-200';
      case 'declined':
        return 'bg-red-100 text-red-700 ring-1 ring-inset ring-red-200';
      case 'expired':
        return 'bg-amber-100 text-amber-700 ring-1 ring-inset ring-amber-200';
      default:
        return 'bg-gray-100 text-gray-600 ring-1 ring-inset ring-gray-300';
    }
  };

  const formatCurrency = (amount: number) =>
    `$${(amount || 0).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-CA', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  // ── AI follow-up for quiet sent quotes ──
  const [fuQuote, setFuQuote] = useState<{ id: number | string; quote_number: string; client_name?: string } | null>(null);
  const [fuSubject, setFuSubject] = useState('');
  const [fuBody, setFuBody] = useState('');
  const [fuBusy, setFuBusy] = useState<'draft' | 'send' | null>(null);
  const [fuMsg, setFuMsg] = useState('');

  const openFollowUp = async (q: { id: number | string; quote_number: string; client_name?: string }) => {
    setFuQuote(q); setFuSubject(''); setFuBody(''); setFuMsg(''); setFuBusy('draft');
    try {
      const r = await apiClient.post(`/quotes/${q.id}/follow-up-draft`, {});
      setFuSubject(r.data.data.subject);
      setFuBody(r.data.data.body);
    } catch { setFuMsg('Draft failed — write it below or try again.'); }
    finally { setFuBusy(null); }
  };
  const sendFollowUp = async () => {
    if (!fuQuote) return;
    setFuBusy('send'); setFuMsg('');
    try {
      await apiClient.post(`/quotes/${fuQuote.id}/follow-up-send`, { subject: fuSubject, body: fuBody });
      setFuMsg('Sent from info@ochrepoint.ca ✓');
      setTimeout(() => setFuQuote(null), 1600);
    } catch (e) {
      setFuMsg((e as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Send failed');
    } finally { setFuBusy(null); }
  };

  const handleCreateQuote = () => {
    navigate('/quotes/new');
  };

  const handleViewQuote = (quote: Quote) => {
    navigate(quotePath(quote.client_id, quote.job_id, quote.id));
  };

  const handleDeleteQuote = async (quoteId: string) => {
    if (!confirm('Are you sure you want to delete this quote?')) return;
    
    try {
      await deleteQuote(quoteId);
      loadQuotes(); // Reload the list
    } catch (error) {
      console.error('Failed to delete quote:', error);
      toast.error('Failed to delete quote');
    }
  };

  const handleCloneQuote = async (quoteId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      setLoading(true);
      const detail = await getQuote(quoteId);
      if (!detail.job_id) { toast.error('Quote has no job — cannot clone'); setLoading(false); return; }
      const newQuote = await createQuote(String(detail.job_id));
      const sectionsData = (detail.sections || []).map((section: any, si: number) => ({
        name: section.name || section.title,
        display_order: si,
        items: (detail.items || [])
          .filter((item: any) => item.section_id === section.id || item.section_id === String(section.id))
          .map((item: any, ii: number) => ({
            item_name: item.item_name || item.name,
            description: item.description,
            quantity: item.quantity,
            unit_price: item.unit_price,
            unit: item.unit || 'ea',
            display_order: ii,
          })),
      }));
      await apiClient.put(`/quotes/${newQuote.id}/sections`, { sections: sectionsData });
      await updateQuote(String(newQuote.id), { status: 'draft' });
      toast.success(`Cloned as ${newQuote.quoteNumber || `#${newQuote.id}`}`);
      navigate(quotePath(detail.client_id, detail.job_id, newQuote.id));
    } catch {
      toast.error('Failed to clone quote');
      setLoading(false);
    }
  };

  const handleConvertToInvoice = async (quoteId: string, quoteNumber: string) => {
    try {
      setLoading(true);
      const result = await createInvoiceFromQuote(parseInt(quoteId));
      toast.success(`Invoice ${result.invoiceNumber} created from Quote ${quoteNumber}`);
      loadQuotes(); // Refresh to update status if needed
    } catch (error) {
      console.error('Failed to convert quote to invoice:', error);
      toast.error('Failed to create invoice from quote');
    } finally {
      setLoading(false);
    }
  };

  const totalPages = Math.ceil(totalItems / itemsPerPage);

  return (
    <MainLayout>
      <div className="flex flex-col gap-4 p-0">
        {/* Hidden Printable Area */}
        <div style={{ position: 'absolute', left: '-9999px', top: 0, backgroundColor: '#ffffff' }}>
          {printingQuote && (
            <PrintableQuote
              ref={printableRef}
              quote={printingQuote}
              companyInfo={companyInfo}
            />
          )}
        </div>

        {/* iOS Large Title Header */}
        <LargeTitleHeader
          title="Quotes & Estimates"
          subtitle="Finance"
          actions={
            <div className="flex gap-2">
              <button
                onClick={() => setShowLabourModal(true)}
                className="flex items-center gap-1.5 h-9 px-3 rounded-lg border border-gray-200 bg-white text-gray-700 text-sm font-medium hover:bg-gray-50 transition-colors"
              >
                <span className="material-symbols-outlined text-base">engineering</span>
                <span className="hidden sm:inline">Labour</span>
              </button>
              <button
                onClick={() => navigate('/quotes/wizard')}
                className="flex items-center gap-1.5 h-9 px-3 rounded-lg bg-amber-500 text-white text-sm font-bold hover:bg-amber-600 transition-colors"
              >
                <span className="material-symbols-outlined text-base">auto_awesome</span>
                <span className="hidden sm:inline">AI Wizard</span>
              </button>
              <button
                onClick={handleCreateQuote}
                className="flex items-center gap-1.5 h-9 px-3 rounded-lg bg-primary text-white text-sm font-bold hover:bg-primary/90 transition-colors"
              >
                <span className="material-symbols-outlined text-base">add</span>
                <span className="hidden sm:inline">New Quote</span>
              </button>
            </div>
          }
        >
          {/* SegmentedControl replaces the old dropdown status filter */}
          <SegmentedControl<StatusFilter>
            options={STATUS_FILTER_OPTIONS}
            value={statusFilter}
            onChange={v => { setStatusFilter(v); setCurrentPage(1); }}
            size="sm"
          />
        </LargeTitleHeader>

        {/* Search */}
        <div className="relative">
          <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-base">search</span>
          <input
            type="text"
            className="w-full pl-9 pr-4 h-10 border border-gray-200 bg-white rounded-lg text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
            placeholder="Search quotes..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        {/* Quotes List */}
        <div className="rounded-xl bg-white border border-gray-200 overflow-hidden">
          {loading ? (
            <div className="px-4 py-12 text-center text-gray-600">
              <span className="material-symbols-outlined animate-spin text-3xl text-gray-300 block mx-auto mb-2">progress_activity</span>
              Loading quotes...
            </div>
          ) : quotes.length === 0 ? (
            <div className="px-4 py-12 text-center text-gray-500">No quotes found</div>
          ) : (
            <PullToRefresh onRefresh={loadQuotes}>
              {/* Desktop table */}
              <div className="hidden sm:block overflow-x-auto">
                <table className="w-full">
                  <thead className="border-b border-gray-200 bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">Quote #</th>
                      <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">Job / Client</th>
                      <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-wider hidden md:table-cell">Date</th>
                      <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">Total</th>
                      <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">Status</th>
                      <th className="px-4 py-3 text-right text-xs font-bold text-gray-600 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {quotes.map((quote) => (
                      <tr
                        key={quote.id}
                        onClick={() => handleViewQuote(quote)}
                        className="hover:bg-primary/5 cursor-pointer transition-colors"
                      >
                        <td className="px-4 py-3 text-sm font-bold text-primary">{quote.quote_number}</td>
                        <td className="px-4 py-3">
                          <div className="flex flex-col">
                            <span className="text-sm font-medium text-gray-900">{quote.job_title || 'Untitled Job'}</span>
                            <span className="text-xs text-gray-600">{quote.client_name || 'Unknown Client'}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 hidden md:table-cell">{formatDate(quote.created_at)}</td>
                        <td className="px-4 py-3 text-sm font-semibold text-gray-900">{formatCurrency(quote.total)}</td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${getStatusColor(quote.status)}`}>
                            {quote.status}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-2">
                            {quote.status?.toLowerCase() === 'sent' && (
                              <button
                                onClick={(e) => { e.stopPropagation(); openFollowUp(quote); }}
                                className="p-2 text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
                                title="AI follow-up email"
                              >
                                <span className="material-symbols-outlined text-base">forward_to_inbox</span>
                              </button>
                            )}
                            {quote.status?.toLowerCase() === 'approved' && (
                              <button
                                onClick={(e) => { e.stopPropagation(); handleConvertToInvoice(quote.id, quote.quote_number); }}
                                className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                                title="Convert to Invoice"
                              >
                                <span className="material-symbols-outlined text-base">receipt_long</span>
                              </button>
                            )}
                            <button
                              onClick={(e) => { e.stopPropagation(); handleViewQuote(quote); }}
                              className="p-2 text-gray-600 hover:text-primary hover:bg-primary/10 rounded-lg transition-colors"
                              title="View/Edit"
                            >
                              <span className="material-symbols-outlined text-base">edit</span>
                            </button>
                            <button
                              onClick={(e) => handleCloneQuote(quote.id, e)}
                              className="p-2 text-gray-600 hover:text-primary hover:bg-primary/10 rounded-lg transition-colors"
                              title="Clone Quote"
                            >
                              <span className="material-symbols-outlined text-base">content_copy</span>
                            </button>
                            <button
                              onClick={(e) => { e.stopPropagation(); handlePrintQuote(quote.id); }}
                              className="p-2 text-gray-600 hover:text-primary hover:bg-primary/10 rounded-lg transition-colors"
                              title="Print Quote"
                            >
                              <span className="material-symbols-outlined text-base">print</span>
                            </button>
                            <button
                              onClick={(e) => { e.stopPropagation(); handleDeleteQuote(quote.id); }}
                              className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              title="Delete"
                            >
                              <span className="material-symbols-outlined text-base">delete</span>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile card list with SwipeableRow */}
              <div className="sm:hidden divide-y divide-gray-50">
                {quotes.map((quote) => {
                  const rowActions: SwipeAction[] = [
                    ...(quote.status?.toLowerCase() === 'approved' ? [{
                      key: 'invoice',
                      label: 'Invoice',
                      icon: 'receipt_long',
                      variant: 'default' as const,
                      onClick: () => handleConvertToInvoice(quote.id, quote.quote_number),
                    }] : []),
                    {
                      key: 'print',
                      label: 'Print',
                      icon: 'print',
                      variant: 'neutral' as const,
                      onClick: () => handlePrintQuote(quote.id),
                    },
                    {
                      key: 'delete',
                      label: 'Delete',
                      icon: 'delete',
                      variant: 'danger' as const,
                      onClick: () => handleDeleteQuote(quote.id),
                    },
                  ];
                  return (
                    <SwipeableRow key={quote.id} actions={rowActions}>
                      <div
                        className="px-4 py-3.5 cursor-pointer"
                        onClick={() => handleViewQuote(quote)}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <p className="font-bold text-primary text-sm">{quote.quote_number}</p>
                            <p className="font-medium text-gray-900 text-sm truncate">{quote.job_title || 'Untitled Job'}</p>
                            <p className="text-xs text-gray-400 truncate">{quote.client_name || 'Unknown Client'}</p>
                          </div>
                          <div className="text-right flex-shrink-0">
                            <p className="font-semibold text-gray-900 text-sm">{formatCurrency(quote.total)}</p>
                            <span className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${getStatusColor(quote.status)}`}>
                              {quote.status}
                            </span>
                          </div>
                        </div>
                      </div>
                    </SwipeableRow>
                  );
                })}
              </div>
              {/* Follow-up modal */}
      {fuQuote && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setFuQuote(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-5 space-y-3" onClick={e => e.stopPropagation()}>
            <h3 className="font-bold text-gray-900 text-sm flex items-center gap-1.5">
              <span className="material-symbols-outlined text-[#C77D3F]">forward_to_inbox</span>
              Follow up on {fuQuote.quote_number}
              {fuBusy === 'draft' && <span className="material-symbols-outlined animate-spin text-base text-gray-400">progress_activity</span>}
            </h3>
            <p className="text-[11px] text-gray-400">AI drafts it in your voice — edit anything, then it sends from info@ochrepoint.ca to {fuQuote.client_name || 'the client'} with reply-to set to you.</p>
            <input value={fuSubject} onChange={e => setFuSubject(e.target.value)} placeholder={fuBusy === 'draft' ? 'Drafting…' : 'Subject'}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-semibold focus:outline-none" />
            <textarea value={fuBody} onChange={e => setFuBody(e.target.value)} rows={8} placeholder={fuBusy === 'draft' ? 'Drafting…' : 'Body'}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none leading-relaxed" />
            {fuMsg && <p className={`text-xs font-semibold ${fuMsg.includes('✓') ? 'text-green-700' : 'text-red-600'}`}>{fuMsg}</p>}
            <div className="flex gap-2">
              <button onClick={() => setFuQuote(null)} className="flex-1 h-10 rounded-lg border border-gray-300 text-sm font-semibold text-gray-600">Cancel</button>
              <button onClick={() => openFollowUp(fuQuote)} disabled={fuBusy !== null}
                className="h-10 px-3 rounded-lg border border-[#C77D3F]/40 text-sm font-semibold text-[#C77D3F] disabled:opacity-50">Redraft</button>
              <button onClick={sendFollowUp} disabled={fuBusy !== null || !fuSubject.trim() || !fuBody.trim()}
                className="flex-1 h-10 rounded-lg bg-[#C77D3F] text-white text-sm font-bold disabled:opacity-40">
                {fuBusy === 'send' ? 'Sending…' : 'Send'}
              </button>
            </div>
          </div>
        </div>
      )}
    </PullToRefresh>
          )}
        </div>

        {/* Pagination */}
        {!loading && totalItems > 0 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalItems}
            itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
          />
        )}
      </div>

      {/* Labour Estimate Modal */}
      {showLabourModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">
                  New Labour Estimate
                </h2>
                <p className="text-sm text-gray-600 mt-1">
                  Create a labour estimate for a job
                </p>
              </div>
              <button
                onClick={() => setShowLabourModal(false)}
                className="text-gray-600 hover:text-gray-900"
              >
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={labourFormData.title}
                  onChange={(e) => setLabourFormData({ ...labourFormData, title: e.target.value })}
                  placeholder="e.g., Framing Labour - Main Floor"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Client <span className="text-red-500">*</span>
                </label>
                <select
                  value={labourFormData.client_id}
                  onChange={(e) => setLabourFormData({ ...labourFormData, client_id: e.target.value, job_id: '' })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                >
                  <option value="">Select a client...</option>
                  {clients.map((client) => (
                    <option key={client.id} value={client.id}>
                      {client.company || client.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Job <span className="text-red-500">*</span>
                </label>
                <select
                  value={labourFormData.job_id}
                  onChange={(e) => setLabourFormData({ ...labourFormData, job_id: e.target.value })}
                  disabled={!labourFormData.client_id}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary disabled:bg-gray-100 disabled:cursor-not-allowed"
                >
                  <option value="">{labourFormData.client_id ? 'Select a job...' : 'Select a client first'}</option>
                  {filteredJobs.map((job) => (
                    <option key={job.id} value={job.id}>
                      {job.jobNumber} - {job.title}
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Total Hours <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    step="0.5"
                    min="0"
                    value={labourFormData.total_hours}
                    onChange={(e) => setLabourFormData({ ...labourFormData, total_hours: e.target.value })}
                    placeholder="e.g., 40"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Hourly Rate ($) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={labourFormData.hourly_rate}
                    onChange={(e) => setLabourFormData({ ...labourFormData, hourly_rate: e.target.value })}
                    placeholder="e.g., 75"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                  />
                </div>
              </div>

              {labourFormData.total_hours && labourFormData.hourly_rate && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Estimated Total:</span>
                    <span className="text-xl font-bold text-primary">
                      {formatCurrency(parseFloat(labourFormData.total_hours) * parseFloat(labourFormData.hourly_rate))}
                    </span>
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notes
                </label>
                <textarea
                  value={labourFormData.notes}
                  onChange={(e) => setLabourFormData({ ...labourFormData, notes: e.target.value })}
                  placeholder="Additional notes about the labour estimate..."
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => setShowLabourModal(false)}
                  disabled={creatingLabour}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateLabourEstimate}
                  disabled={creatingLabour}
                  className="flex-1 px-4 py-2 bg-primary text-white rounded-lg hover:opacity-90 transition-opacity font-medium disabled:opacity-50"
                >
                  {creatingLabour ? 'Creating...' : 'Create Estimate'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </MainLayout>
  );
};


